import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { E621Service } from '../../services/e621.service';
import { SlideshowService } from '../../services/slideshow.service';
import { FavoritesService } from '../../services/favorites.service';
import { SeenPersistenceService } from '../../services/seen-persistence.service';
import { AutoLoopService } from '../../services/auto-loop.service';
import { FilterGroupService } from '../../services/filter-group.service';
import { ColorThemeService } from '../../services/color-theme.service';
import { SearchSettingsService } from '../../services/search-settings.service';
import { SearchHistoryService } from '../../services/search-history.service';
import { SearchDateService } from '../../services/search-date.service';
import { SearchUrlService } from '../../services/search-url.service';
import {
  ApiSettings,
  FilterGroup,
} from '../../models/e621.models';

@Component({
  selector: 'app-search',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './search.component.html',
  styleUrl: './search.component.scss',
})
export class SearchComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private e621Service = inject(E621Service);
  private slideshowService = inject(SlideshowService);
  private favoritesService = inject(FavoritesService);
  private seenPersistenceService = inject(SeenPersistenceService);
  private autoLoopService = inject(AutoLoopService);
  private filterGroupService = inject(FilterGroupService);
  private colorThemeService = inject(ColorThemeService);
  private searchSettingsService = inject(SearchSettingsService);
  private searchHistoryService = inject(SearchHistoryService);
  private searchDateService = inject(SearchDateService);
  private searchUrlService = inject(SearchUrlService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  searchForm: FormGroup;
  settingsForm: FormGroup;
  searchOptionsForm: FormGroup;
  showSettings = false;
  showSearchOptions = false;
  showFilterGroups = false;
  private _isSearching = false;
  authWarning: string | null = null;

  // Flag to track if the next search should open in fullscreen
  private shouldOpenFullscreen = false;

  // Subject used to cancel subscriptions when the component is destroyed.
  private readonly destroy$ = new Subject<void>();

  // Tracks the last direction the user iterated date periods ([=prev, ]=next).
  // Used to continue iteration automatically when the video queue is exhausted.
  private lastDateShiftDirection: 'prev' | 'next' | null = null;

  /**
   * Base query (no generated date/order/score tags) behind the queue currently
   * loaded in the slideshow.  Search history is keyed by the base query, while
   * the slideshow only knows the final query, so position updates have to be
   * re-keyed through this.
   */
  private activeBaseQuery: string | null = null;

  // Fullscreen mode selector ('windowed' or 'native')
  fullscreenMode: 'windowed' | 'native' = 'windowed';

  // Favorites tracking
  favoritesEnabled = false;
  favoritesCount = 0;

  // Seen persistence tracking
  seenVideosCount = 0;

  // Filter group state
  filterGroupSearch = '';
  allFilterGroups: FilterGroup[] = [];
  renamingGroupId: string | null = null;
  renamingGroupName = '';
  isAddingFilterGroup = false;
  newFilterGroupName = '';

  // Getter for isSearching
  get isSearching(): boolean {
    return this._isSearching;
  }

  // Setter for isSearching
  set isSearching(value: boolean) {
    this._isSearching = value;
  }
  // Default search tags when user doesn't specify any
  private readonly DEFAULT_TAGS = 'score:>500 order:random date:>1_week_ago';

  // Delegate color themes and defaults to service
  get colorThemes() {
    return this.colorThemeService.colorThemes;
  }
  get DEFAULT_PRIMARY_COLOR() {
    return this.colorThemeService.DEFAULT_PRIMARY_COLOR;
  }
  get DEFAULT_SECONDARY_COLOR() {
    return this.colorThemeService.DEFAULT_SECONDARY_COLOR;
  }
  get DEFAULT_TERTIARY_COLOR() {
    return this.colorThemeService.DEFAULT_TERTIARY_COLOR;
  }
  get DEFAULT_BACKGROUND_COLOR() {
    return this.colorThemeService.DEFAULT_BACKGROUND_COLOR;
  }

  constructor() {
    this.searchForm = this.fb.group({
      query: ['', Validators.required],
    });    this.settingsForm = this.fb.group({
      username: [''],
      apiKey: [''],
      blacklistText: [''],
      selectedTheme: [''], // For theme dropdown
      primaryColor: ['#bb86fc'],  // OLED default purple
      secondaryColor: ['#000000'], // Pure black for OLED
      tertiaryColor: ['#ff9500'],  // OLED orange accent
      backgroundColor: ['#000000'], // OLED background
      // Persistence settings
      persistVolume: [true], // Volume persistence enabled by default
      enableSeenTracking: [true], // Seen tracking enabled by default
      enableSeenBlacklist: [false], // Seen blacklist disabled by default
      persistQueue: [true], // Queue persistence enabled by default
      // Auto-loop settings
      autoLoopEnabled: [true], // Auto-loop enabled by default
      autoLoopDuration: [20, [Validators.min(5)]], // Default 20 seconds, minimum 5
      // Image settings
      imageDisplayDuration: [5, [Validators.min(1), Validators.max(60)]], // Default 5 seconds, 1-60 range
      imageAutoAdvance: [true], // Auto-advance for images enabled by default
      animatedGifBehavior: ['once'], // 'loop', 'once', 'timed'
      pinchZoomEnabled: [false], // Pinch-to-zoom for images (experimental, disabled by default)
      // Fullscreen controls position
      fullscreenControlsPosition: ['bottom-right'], // Default bottom-right
    });

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const currentDay = now.getDate();

    this.searchOptionsForm = this.fb.group({
      // Advanced date controls
      advancedDateMode: ['none'],        // 'none' | 'period' | 'exact' | 'range'
      // Period mode ('last N rolling window' with offset)
      periodUnit: ['week'],              // 'today' | 'day' | 'week' | 'month' | 'year'
      periodOffset: [0],                 // 0 = current, 1+ = shifted further back
      // Exact date mode
      exactGranularity: ['day'],         // 'year' | 'month' | 'day'
      exactYear: [currentYear],
      exactMonth: [currentMonth],
      exactDay: [currentDay],
      // Range mode
      rangeGranularity: ['month'],       // 'year' | 'month' | 'day'
      rangeFromYear: [currentYear - 1],
      rangeFromMonth: [currentMonth],
      rangeFromDay: [currentDay],
      rangeToYear: [currentYear],
      rangeToMonth: [currentMonth],
      rangeToDay: [currentDay],
      // Other search options
      orderBy: [''],
      minScore: [null],
      maxScore: [null],
    });
  }
  ngOnInit(): void {
    // Load fullscreen mode preference
    const savedMode = localStorage.getItem('preferredFullscreenMode') as 'windowed' | 'native';
    if (savedMode) {
      this.fullscreenMode = savedMode;
    }

    // Monitor favorites service
    this.favoritesService.getIsEnabled().subscribe(enabled => {
      this.favoritesEnabled = enabled;
    });

    this.favoritesService.getFavorites().subscribe(favorites => {
      this.favoritesCount = favorites.length;
    });

    // Monitor seen media count
    this.seenPersistenceService.getSeenVideosCount().subscribe(count => {
      this.seenVideosCount = count;
    });

    // Subscribe to filter groups
    this.filterGroupService.getFilterGroups().pipe(takeUntil(this.destroy$)).subscribe(groups => {
      this.allFilterGroups = groups;
    });

    // Load existing API settings
    this.e621Service.getApiSettings().subscribe((settings) => {
      // Convert blacklist array to text for the textarea
      const blacklistText = settings.blacklist ? settings.blacklist.join('\n') : '';
      this.settingsForm.patchValue({
        ...settings,
        blacklistText,
      });
    });

    // Subscribe to position changes from slideshow service.
    // The emitted query is the final query (with generated date/order/score
    // tags); history is keyed by the base query, so use the tracked one.
    this.slideshowService
      .getPositionChanges()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ position, videoId }) => {
        if (!this.activeBaseQuery) return;
        this.updateSearchPosition(this.activeBaseQuery, position, videoId);
      });

    // When the video queue is exhausted, continue iterating the date range.
    // If the user previously used [ or ] we continue in that direction.
    // Otherwise, if "later" is unavailable (already at the most recent
    // window), presume the user wants to go earlier.
    this.slideshowService.getQueueComplete().pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.isDateControlActive) {
        if (this.lastDateShiftDirection !== null) {
          console.log(`[DATE ITERATE] Queue complete – continuing in '${this.lastDateShiftDirection}' direction`);
          this.shiftDatePeriod(this.lastDateShiftDirection, true);
        } else if (!this.canShiftLater) {
          console.log('[DATE ITERATE] Queue complete – presuming earlier (no later available)');
          this.shiftDatePeriod('prev', true);
        }
      }
    });    // Check for 'tags' query parameter and populate search field
    // This takes precedence over resuming last session
    const hasUrlParams = this.loadFromUrlParameters();

    // Resume last session only if no URL parameters were present
    if (!hasUrlParams) {
      console.log('No URL parameters found, attempting to resume last session');
      this.resumeLastSession();
    } else {
      console.log('URL parameters found, skipping last session resume (query string takes precedence)');
    }

    // Execute search after both URL and storage logic is complete
    setTimeout(() => {
      const currentQuery = this.searchForm.get('query')?.value?.trim();
      if (currentQuery) {
        console.log('Executing search with final query:', currentQuery);
        this.onSearch();
      }
    }, 150);    // Load color settings and apply them
    const { primaryColor, secondaryColor, tertiaryColor, backgroundColor } = this.colorThemeService.loadColorSettings();
    this.settingsForm.patchValue({ primaryColor, secondaryColor, tertiaryColor, backgroundColor });
    this.colorThemeService.applyColorSettings(primaryColor, secondaryColor, tertiaryColor, backgroundColor);

    // Load persistence settings
    const { persistVolume } = this.searchSettingsService.loadPersistenceSettings();
    const { enableSeenTracking, enableSeenBlacklist } = this.seenPersistenceService.getSeenSettings();
    const { enabled: autoLoopEnabled, duration: autoLoopDuration } = this.autoLoopService.getSettings();
    const { fullscreenControlsPosition } = this.searchSettingsService.loadVideoSettings();
    const { imageDisplayDuration, imageAutoAdvance, animatedGifBehavior, pinchZoomEnabled } = this.searchSettingsService.loadImageSettings();
    this.settingsForm.patchValue({ 
      persistVolume, 
      enableSeenTracking, 
      enableSeenBlacklist, 
      autoLoopEnabled, 
      autoLoopDuration, 
      fullscreenControlsPosition,
      imageDisplayDuration,
      imageAutoAdvance,
      animatedGifBehavior,
      pinchZoomEnabled
    });

    // Load queue persistence setting
    const persistQueue = this.searchSettingsService.loadQueuePersistenceSetting();
    this.settingsForm.patchValue({ persistQueue });
  }  /**
   * Execute search
   * @param options.updateUrl Set false to leave the browser URL untouched
   *        (used by automatic date iteration, which is not user intent).
   */
  onSearch(options: { updateUrl?: boolean } = {}): void {
    if (!this.searchForm.valid) {
      return;
    }

    let baseQuery = this.sanitizeQuery(this.searchForm.get('query')?.value ?? '');
    
    // If no tags provided, use default tags
    if (!baseQuery) {
      baseQuery = this.DEFAULT_TAGS;
      console.log('No tags provided, using default tags:', baseQuery);
    }

    // Update URL with tags parameter to enable bookmarking.
    // Auto-iteration opts out: the URL should keep the window the user chose,
    // not the one the slideshow drifted to on its own.
    if (options.updateUrl !== false) {
      this.updateUrlWithTags(baseQuery);
    }

    // If already searching, we allow the new search to override the previous one
    if (this.isSearching) {
      console.log('Already searching, cancelling previous search to start new one');
    }

    // Build the final query by appending search options
    const finalQuery = this.buildFinalSearchQuery(baseQuery);

    console.log('Executing search with base query:', baseQuery);
    console.log('Final query with options:', finalQuery);

    this.isSearching = true;
    // Clear any previous auth warnings at the start of a new search
    this.authWarning = null;

    // Check if this search exists in history BEFORE doing anything else
    // Use the base query AND current search options for full-context matching
    // Position resume is only valid when the full search context matches
    const currentOptions = this.searchUrlService.getCurrentSearchOptions(this.searchOptionsForm.value);
    const existingHistory = this.searchHistoryService.findByFullContext(baseQuery, currentOptions);

    const searchParams = { tags: finalQuery, page: 1, limit: 200 };

    this.e621Service.searchVideos(searchParams).subscribe({
      next: (result) => {
        console.log(
          `Search completed, found ${result.videos.length} videos`
        );

        // Clear spinner immediately when search completes
        this.clearSpinnerState();

        // Clear any previous auth warnings
        this.authWarning = null;

        // Check if any videos were filtered due to auth requirements
        if (result.authRequiredCount > 0) {
          this.authWarning = `${result.authRequiredCount} video(s) require authentication to view. Please set your API credentials in settings to access all content.`;
        }

        if (result.videos.length > 0) {
          // Claim the queue for this base query before loading, so the
          // position emitted by loadVideosWithSearch is attributed correctly.
          this.activeBaseQuery = baseQuery;

          this.slideshowService.loadVideosWithSearch(
            result.videos,
            searchParams,
            {
              totalFound: result.totalFound,
              authRequiredCount: result.authRequiredCount,
              blacklistedCount: result.blacklistedCount || 0
            }
          );

          // Save search history immediately, then handle position restoration separately
          this.saveSearchHistory(baseQuery);

          // If this search exists in history and has explicit order, try to restore position
          if (
            existingHistory &&
            existingHistory.hasExplicitOrder
          ) {
            // Prefer ID-based restore over position-based restore
            if (existingHistory.lastViewedVideoId) {
              console.log(
                `[RESUME] Restoring search "${baseQuery}" to previous video ID ${existingHistory.lastViewedVideoId} (page ${existingHistory.currentPage || 1})`
              );
              console.log('[RESUME] Setting up setTimeout for video ID restoration');
              // Use setTimeout to ensure the slideshow is fully loaded before restoring position
              setTimeout(() => {
                console.log('[RESUME] setTimeout callback executing - about to call restoreToVideoId');
                this.slideshowService
                  .restoreToVideoId(existingHistory.lastViewedVideoId!, searchParams)
                  .then((success) => {
                    if (success) {
                      console.log(
                        `[RESUME] Successfully restored to video ID ${existingHistory.lastViewedVideoId}`
                      );
                    } else {
                      console.log(
                        `[RESUME] Failed to restore to video ID ${existingHistory.lastViewedVideoId}, falling back to position restore`
                      );
                      // Fallback to position-based restore if ID-based fails
                      if (existingHistory.lastViewedPosition) {
                        this.slideshowService.restoreToPosition(existingHistory.lastViewedPosition)
                          .catch((posError) => {
                            console.error('[RESUME] Position-based restore fallback failed:', posError);
                          });
                      }
                    }
                  })
                  .catch((error) => {
                    console.error('[RESUME] Failed to restore video ID:', error);
                    // Fallback to position-based restore if ID-based fails
                    if (existingHistory.lastViewedPosition) {
                      console.log('[RESUME] Falling back to position-based restore');
                      this.slideshowService.restoreToPosition(existingHistory.lastViewedPosition)
                        .catch((posError) => {
                          console.error('[RESUME] Position-based restore also failed:', posError);
                        });
                    }
                  });
              }, 100);
            } else if (existingHistory.lastViewedPosition) {
              // Fallback to position-based restore for backwards compatibility
              console.log(
                `[RESUME] No video ID available, restoring search "${baseQuery}" to previous position ${existingHistory.lastViewedPosition} (page ${existingHistory.currentPage || 1})`
              );
              console.log('[RESUME] Setting up setTimeout for position restoration');
              setTimeout(() => {
                console.log('[RESUME] setTimeout callback executing - about to call restoreToPosition');
                this.slideshowService
                  .restoreToPosition(existingHistory.lastViewedPosition!)
                  .then(() => {
                    console.log(
                      `[RESUME] Successfully restored to position ${existingHistory.lastViewedPosition}`
                    );
                  })
                  .catch((error) => {
                    console.error('[RESUME] Failed to restore position:', error);
                  });
              }, 100);
            }
          } else {
            console.log('[RESUME] No existing history to restore');
            console.log('[RESUME] existingHistory:', existingHistory);
          }

          this.slideshowService.start();

          // Smooth scroll to video player if this is not a fullscreen search
          if (!this.shouldOpenFullscreen) {
            this.scrollToVideoPlayer();
          }

          // Add a final verification that spinner is cleared
          setTimeout(() => {
            if (this._isSearching) {
              console.error('ERROR: Spinner still showing after search completion!');
              this.clearSpinnerState();
            }
          }, 200);        } else {
          console.warn('No videos found for query:', finalQuery);

          // Clear the slideshow to prevent showing previous results
          this.slideshowService.clear();

          if (result.authRequiredCount > 0) {
            this.authWarning = `All ${result.authRequiredCount} video(s) found require authentication. Please set your API credentials in settings.`;
          } else {
            this.authWarning = `No videos found for search: "${baseQuery}". Try different search terms or check your filters.`;
          }
          // Save history even if no videos found (use base query)
          this.saveSearchHistory(baseQuery);
        }
      },      error: (error) => {
        console.error('Search API call failed with error:', error);
        // Always clear isSearching flag on error
        this.clearSpinnerState();

        // Clear slideshow to prevent showing previous results
        this.slideshowService.clear();

        // Set error message
        this.authWarning = 'Search failed. Please check your connection and try again.';
      },
    });
  }
  /**
   * Handle form submission
   */
  onSubmit(): void {
    this.onSearch();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle fullscreen search
   */
  onFullscreenSearch(): void {
    this.shouldOpenFullscreen = true;
    this.onSearch();
  }

  /**
   * Check if the next video should open in fullscreen and reset the flag
   * Returns both the flag and the preferred fullscreen mode
   */
  checkAndResetFullscreenFlag(): { shouldOpen: boolean; mode: 'windowed' | 'native' } {
    const shouldOpen = this.shouldOpenFullscreen;
    this.shouldOpenFullscreen = false;
    return { shouldOpen, mode: this.fullscreenMode };
  }

  /**
   * Handle keyboard shortcuts in textarea
   */
  onTextareaKeydown(event: KeyboardEvent): void {
    // Ctrl+Enter for fullscreen search
    if (event.ctrlKey && event.key === 'Enter') {
      event.preventDefault(); // Prevent default newline behavior
      this.onFullscreenSearch();
    }
    // Shift+Enter for regular search
    else if (event.shiftKey && event.key === 'Enter') {
      event.preventDefault(); // Prevent default newline behavior
      this.onSubmit();
    }
  }

  /**
   * Clear search textarea and focus on it
   */
  clearSearchAndFocus(): void {
    // Clear the search query
    this.searchForm.get('query')?.setValue('');

    // Focus on the textarea after a short delay to ensure the DOM is updated
    setTimeout(() => {
      const textarea = document.querySelector('.search-textarea') as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
      }
    }, 10);
  }

  /**
   * Toggle settings panel
   */
  toggleSettings(): void {
    this.showSettings = !this.showSettings;
  }

  /**
   * Save API settings
   */
  saveSettings(): void {
    if (this.settingsForm.valid) {
      // Get current settings to check for changes
      const currentSettings = this.e621Service.getCurrentApiSettings();

      // Parse blacklist text into array
      const blacklistText = this.settingsForm.get('blacklistText')?.value || '';
      const newBlacklist = blacklistText
        .split(/[\s\n]+/)
        .map((tag: string) => tag.trim())
        .filter((tag: string) => tag.length > 0);

      const newSettings: ApiSettings = {
        username: this.settingsForm.get('username')?.value || undefined,
        apiKey: this.settingsForm.get('apiKey')?.value || undefined,
        blacklist: newBlacklist.length > 0 ? newBlacklist : undefined,
      };

      // Check if any settings that affect search results have changed
      const blacklistChanged = JSON.stringify(currentSettings.blacklist || []) !== JSON.stringify(newBlacklist);
      const usernameChanged = (currentSettings.username || '') !== (newSettings.username || '');
      const apiKeyChanged = (currentSettings.apiKey || '') !== (newSettings.apiKey || '');

      // Any change to authentication or blacklist requires cache clearing
      const settingsChanged = blacklistChanged || usernameChanged || apiKeyChanged;

      if (settingsChanged) {
        // Clear cache when any settings that affect search results change
        this.clearCache();
        this.clearSearchHistory();

        const changedItems = [];
        if (blacklistChanged) changedItems.push('blacklist');
        if (usernameChanged) changedItems.push('username');
        if (apiKeyChanged) changedItems.push('API key');

        console.log(`Settings changed (${changedItems.join(', ')}), cache and search history cleared`);
      }

      // Save API settings
      this.e621Service.saveApiSettings(newSettings);

      // Save color settings
      const primaryColor = this.settingsForm.get('primaryColor')?.value || this.DEFAULT_PRIMARY_COLOR;
      const secondaryColor = this.settingsForm.get('secondaryColor')?.value || this.DEFAULT_SECONDARY_COLOR;
      const tertiaryColor = this.settingsForm.get('tertiaryColor')?.value || this.DEFAULT_TERTIARY_COLOR;
      const backgroundColor = this.settingsForm.get('backgroundColor')?.value || this.DEFAULT_BACKGROUND_COLOR;
      this.saveColorSettings(primaryColor, secondaryColor, tertiaryColor, backgroundColor);

      // Save auto-loop settings
      const autoLoopEnabled = this.settingsForm.get('autoLoopEnabled')?.value !== false;
      const autoLoopDuration = Math.max(5, this.settingsForm.get('autoLoopDuration')?.value || 20);
      this.autoLoopService.saveSettings({ enabled: autoLoopEnabled, duration: autoLoopDuration });

      // Save video settings
      const fullscreenControlsPosition = this.settingsForm.get('fullscreenControlsPosition')?.value || 'bottom-right';
      this.saveVideoSettings({ fullscreenControlsPosition });

      // Save image settings
      const imageDisplayDuration = Math.max(1, Math.min(60, this.settingsForm.get('imageDisplayDuration')?.value || 5));
      const imageAutoAdvance = this.settingsForm.get('imageAutoAdvance')?.value !== false;
      const animatedGifBehavior = this.settingsForm.get('animatedGifBehavior')?.value || 'once';
      const pinchZoomEnabled = this.settingsForm.get('pinchZoomEnabled')?.value === true;
      this.saveImageSettings({ imageDisplayDuration, imageAutoAdvance, animatedGifBehavior, pinchZoomEnabled });

      this.showSettings = false;
      console.log('Settings saved');
    }
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.e621Service.clearCache();
    console.log('Cache cleared');
  }
  /**
   * Execute search with a specific query (used by other components)
   */
  executeSearch(query: string): void {
    if (!query?.trim()) return;

    // Update the form with the new query
    this.searchForm.patchValue({ query: query.trim() });

    // Force form validation update
    this.searchForm.updateValueAndValidity();

    // Use setTimeout to ensure the form state is updated before executing search
    setTimeout(() => {
      this.onSearch();
    }, 0);
  }

  /**
   * Append a tag to the current search and execute
   */
  appendTagToSearch(tag: string): void {
    const currentQuery = this.searchForm.get('query')?.value?.trim() || '';
    const newQuery = currentQuery ? `${currentQuery} ${tag}` : tag;
    this.executeSearch(newQuery);
  }

  /**
   * Handle click on favorites status to filter user's favorites
   */
  onFavoritesClick(): void {
    const username = this.settingsForm.get('username')?.value?.trim();
    if (!username) {
      console.warn('No username configured for favorites filter');
      return;
    }

    const favoritesTag = `fav:${username}`;
    this.appendTagToSearch(favoritesTag);
  }
  /**
   * Get cache stats for debugging
   */
  getCacheStats(): void {
    const stats = this.e621Service.getCacheStats();
    console.log('Cache stats:', stats);
  }

  /**
   * Check if search button should be disabled
   */
  get isSearchDisabled(): boolean {
    const isInvalid = this.searchForm.invalid;
    const isSearching = this.isSearching;
    const query = this.searchForm.get('query')?.value?.trim();
    return isInvalid || isSearching || !query;
  }

  /**
   * Check if search options should be shown
   */
  readonly shouldShowSearchOptions = true;

  /**
   * Toggle search options panel
   */
  toggleSearchOptions(): void {
    this.showSearchOptions = !this.showSearchOptions;
  }

  /**
   * Sanitize a search query by converting to lowercase, collapsing multiple spaces, and trimming whitespace
   */
  private sanitizeQuery(query: string): string {
    return query.toLowerCase().replace(/\s+/g, ' ').trim();
  }

  /**
   * Build the final search query by appending search options to the base query
   */
  private buildFinalSearchQuery(baseQuery: string): string {
    const query = this.searchForm.get('query')?.value?.trim() || '';
    return this.searchUrlService.buildFinalSearchQuery(baseQuery, query, this.searchOptionsForm.value);
  }

  /**
   * Apply search options to the current search
   */
  applySearchOptions(): void {
    // Hide search options after applying
    this.showSearchOptions = false;

    // Execute the search (this will automatically use buildFinalSearchQuery)
    this.onSearch();
  }


  /**
   * Reset search options to default values
   */
  resetSearchOptions(): void {
    this.searchOptionsForm.reset(this.searchDateService.getDefaultDateOptions());
  }

  // ─── Advanced Date Controls (delegated to SearchDateService) ───────────────

  /** True when the search query already contains a manual date: tag. */
  get hasManualDateTag(): boolean {
    return this.searchDateService.hasManualDateTag(this.searchForm.get('query')?.value || '');
  }

  /** True when the search query already contains a manual order: tag. */
  get hasManualOrderTag(): boolean {
    return this.searchDateService.hasManualOrderTag(this.searchForm.get('query')?.value || '');
  }

  /** True when the search query already contains a manual score: tag. */
  get hasManualScoreTag(): boolean {
    return this.searchDateService.hasManualScoreTag(this.searchForm.get('query')?.value || '');
  }

  /** Current advanced date mode value. */
  get advancedDateMode(): string {
    return this.searchOptionsForm.get('advancedDateMode')?.value || 'none';
  }

  /** True when a date mode is active and no manual date tag is present. */
  get isDateControlActive(): boolean {
    return this.advancedDateMode !== 'none' && !this.hasManualDateTag;
  }

  /**
   * Whether the current date selection can be shifted "later" (closer to the
   * present).  Returns false when the selection already covers today, meaning
   * there is no meaningful "later" window to move to.
   */
  get canShiftLater(): boolean {
    return this.searchDateService.canShiftLater(this.searchOptionsForm.value);
  }

  /** The e621 date tag generated from the current advanced date settings. */
  get currentDateTag(): string {
    return this.searchDateService.buildAdvancedDateTag(this.searchOptionsForm.value);
  }

  /** Human-readable label for the current exact-date selection. */
  get exactDateLabel(): string {
    const granularity = this.searchOptionsForm.get('exactGranularity')?.value || 'day';
    const year = Number(this.searchOptionsForm.get('exactYear')?.value);
    const month = Number(this.searchOptionsForm.get('exactMonth')?.value);
    const day = Number(this.searchOptionsForm.get('exactDay')?.value);
    return this.searchDateService.getExactDateLabel(granularity, year, month, day);
  }

  /** Human-readable label for the current period offset. */
  get periodOffsetLabel(): string {
    const unit   = this.searchOptionsForm.get('periodUnit')?.value   || 'week';
    const offset = Number(this.searchOptionsForm.get('periodOffset')?.value) || 0;
    return this.searchDateService.getPeriodOffsetLabel(unit, offset);
  }

  /** Human-readable label for the current range selection. */
  get rangeDateLabel(): string {
    const granularity  = this.searchOptionsForm.get('rangeGranularity')?.value  || 'month';
    const fromYear  = Number(this.searchOptionsForm.get('rangeFromYear')?.value);
    const fromMonth = Number(this.searchOptionsForm.get('rangeFromMonth')?.value);
    const fromDay   = Number(this.searchOptionsForm.get('rangeFromDay')?.value);
    const toYear    = Number(this.searchOptionsForm.get('rangeToYear')?.value);
    const toMonth   = Number(this.searchOptionsForm.get('rangeToMonth')?.value);
    const toDay     = Number(this.searchOptionsForm.get('rangeToDay')?.value);
    return this.searchDateService.getRangeDateLabel(granularity, fromYear, fromMonth, fromDay, toYear, toMonth, toDay);
  }

  /**
   * Set the active date mode.
   */
  setDateMode(mode: string): void {
    this.searchOptionsForm.patchValue({ advancedDateMode: mode });
  }

  /**
   * Shift the current date selection one step in the given direction.
   * 'prev' moves further back in time; 'next' moves closer to the present.
   * Triggers a new search automatically.
   * @param isAutoIterate True when the shift was triggered by queue exhaustion
   *        rather than by the user, in which case the URL is left alone.
   */
  shiftDatePeriod(direction: 'prev' | 'next', isAutoIterate = false): void {
    const mode = this.advancedDateMode;
    if (mode === 'none') return;

    this.lastDateShiftDirection = direction;

    const patch = this.searchDateService.shiftDatePeriod(direction, this.searchOptionsForm.value);
    this.searchOptionsForm.patchValue(patch);

    this.onSearch({ updateUrl: !isAutoIterate });
  }

  /**
   * Apply the active date filter and trigger a search.
   */
  applyDateFilter(): void {
    this.onSearch();
  }

  /**
   * Remove any manual date: tags from the search query so the advanced
   * date controls take over.
   */
  removeManualDateTags(): void {
    const current = this.searchForm.get('query')?.value?.trim() || '';
    this.searchForm.patchValue({ query: this.searchDateService.removeManualDateTags(current) });
  }

  /**
   * Remove any manual order: tags from the search query so the advanced
   * order controls take over.
   */
  removeManualOrderTags(): void {
    const current = this.searchForm.get('query')?.value?.trim() || '';
    this.searchForm.patchValue({ query: this.searchDateService.removeManualOrderTags(current) });
  }

  /**
   * Remove any manual score: tags from the search query so the advanced
   * score controls take over.
   */
  removeManualScoreTags(): void {
    const current = this.searchForm.get('query')?.value?.trim() || '';
    this.searchForm.patchValue({ query: this.searchDateService.removeManualScoreTags(current) });
  }

  /**
   * Keyboard shortcut handler: [ = prev period, ] = next period.
   */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
      return;
    }
    if (this.advancedDateMode === 'none' || this.hasManualDateTag) return;

    if (event.key === '[' && !event.ctrlKey && !event.altKey && !event.shiftKey) {
      event.preventDefault();
      this.shiftDatePeriod('prev');
    } else if (event.key === ']' && !event.ctrlKey && !event.altKey && !event.shiftKey) {
      event.preventDefault();
      this.shiftDatePeriod('next');
    }
  }

  // ─── Date Tag Builders (delegated to SearchDateService) ─────────────────────

  /**
   * Save search to history (delegates to SearchHistoryService)
   */
  private saveSearchHistory(query: string, currentPosition?: number): void {
    const currentPage = this.slideshowService.getCurrentPage() || 1;
    const totalItemsLoaded = this.slideshowService.getTotalVideosLoaded() || 0;
    const searchOptions = this.searchUrlService.getCurrentSearchOptions(this.searchOptionsForm.value);
    this.searchHistoryService.saveSearchHistory(query, currentPage, totalItemsLoaded, currentPosition, searchOptions);
  }

  /**
   * Update last viewed position for current search
   */
  updateSearchPosition(query: string, position: number, videoId?: number): void {
    const currentPage = this.slideshowService.getCurrentPage() || 1;
    const totalItemsLoaded = this.slideshowService.getTotalVideosLoaded() || 0;
    this.searchHistoryService.updateSearchPosition(query, position, currentPage, totalItemsLoaded, videoId);
  }

  /**
   * Update URL with tags parameter to enable bookmarking
   */
  private updateUrlWithTags(tags: string): void {
    this.searchUrlService.updateUrl(this.router, this.route, tags, this.searchOptionsForm.value);
  }
  /**
   * Load search query from URL parameters
   * @returns true if URL parameters were found and processed, false otherwise
   */
  private loadFromUrlParameters(): boolean {
    const parsed = this.searchUrlService.parseUrlParameters();
    if (!parsed) return false;

    if (parsed.tags) {
      this.searchForm.patchValue({ query: parsed.tags });
    }
    if (parsed.searchOptions) {
      this.searchOptionsForm.patchValue(parsed.searchOptions);
    }

    // Restore filter group from URL (applies its config, overriding other options)
    if (parsed.filterGroupId) {
      const group = this.filterGroupService.getGroupById(parsed.filterGroupId);
      if (group) {
        this.searchOptionsForm.patchValue(group.config);
        if (group.config.tags) {
          this.searchForm.patchValue({ query: group.config.tags });
        }
        // A positive periodOffset implies date iteration has already started
        // going backwards, so auto-advance should continue in that direction.
        if (group.config.advancedDateMode === 'period' && group.config.periodOffset > 0) {
          this.lastDateShiftDirection = 'prev';
        }
      }
    }

    // Trigger change detection to ensure the form displays the values
    this.cdr.detectChanges();
    return true;
  }
    /**
   * Resume last session by loading the most recent search
   * Only called when no URL parameters are present
   */
  private resumeLastSession(): void {
    try {
      const lastSession = this.searchHistoryService.getLastSession();
      if (!lastSession) {
        console.log('No last session found, applying Drawfiend Mode as default for first visit');
        const drawfiendGroup = this.filterGroupService.getDrawfiendModeGroup();
        if (drawfiendGroup) {
          this.searchOptionsForm.patchValue(drawfiendGroup.config);
          if (drawfiendGroup.config.tags) {
            this.searchForm.patchValue({ query: drawfiendGroup.config.tags });
          }
        }
        return;
      }
      console.log('Resuming last session with search:', lastSession.query);

      // Set the search query in the form
      this.searchForm.patchValue({ query: lastSession.query });

      // Restore all search options if they were saved
      if (lastSession.searchOptions) {
        this.searchOptionsForm.patchValue(lastSession.searchOptions);
        console.log('Restored search options from last session:', lastSession.searchOptions);
      }
    } catch (error) {
      console.error('Error resuming last session:', error);
    }
  }

  /**
   * Clear search history (delegates to SearchHistoryService)
   */
  private clearSearchHistory(): void {
    this.searchHistoryService.clearSearchHistory();
  }

  /**
   * Clear spinner state consistently
   */
  private clearSpinnerState(): void {
    this.isSearching = false;
    this.cdr.detectChanges();
  }

  /**
   * Set the fullscreen mode preference
   */
  setFullscreenMode(mode: 'windowed' | 'native'): void {
    this.fullscreenMode = mode;
    console.log(`Fullscreen mode set to: ${mode}`);

    // Save preference to localStorage
    localStorage.setItem('preferredFullscreenMode', mode);
  }  /**
   * Handle color change from form inputs
   */
  onColorChange(): void {
    const primaryColor = this.settingsForm.get('primaryColor')?.value || this.DEFAULT_PRIMARY_COLOR;
    const secondaryColor = this.settingsForm.get('secondaryColor')?.value || this.DEFAULT_SECONDARY_COLOR;
    const tertiaryColor = this.settingsForm.get('tertiaryColor')?.value || this.DEFAULT_TERTIARY_COLOR;
    const backgroundColor = this.settingsForm.get('backgroundColor')?.value || this.DEFAULT_BACKGROUND_COLOR;

    // Reset theme selection when colors are manually changed
    this.settingsForm.patchValue({ selectedTheme: '' }, { emitEvent: false });

    // Apply colors immediately for live preview
    this.colorThemeService.applyColorSettings(primaryColor, secondaryColor, tertiaryColor, backgroundColor);
  }

  /**
   * Apply a predefined theme
   */
  onThemeChange(): void {
    const themeName = this.settingsForm.get('selectedTheme')?.value;
    if (!themeName) return;

    const theme = this.colorThemeService.findThemeByName(themeName);
    if (theme) {
      // Update the color form controls
      this.settingsForm.patchValue({
        primaryColor: theme.primary,
        secondaryColor: theme.secondary,
        tertiaryColor: theme.tertiary,
        backgroundColor: theme.background
      });

      // Apply the theme immediately for live preview
      this.colorThemeService.applyColorSettings(theme.primary, theme.secondary, theme.tertiary, theme.background);
    }
  }

  /**
   * Save color settings (delegates to ColorThemeService)
   */
  private saveColorSettings(primaryColor: string, secondaryColor: string, tertiaryColor: string, backgroundColor: string): void {
    this.colorThemeService.saveColorSettings(primaryColor, secondaryColor, tertiaryColor, backgroundColor);
  }

  /**
   * Save video settings (delegates to SearchSettingsService)
   */
  private saveVideoSettings(settings: { fullscreenControlsPosition: string }): void {
    this.searchSettingsService.saveVideoSettings(settings);
  }

  /**
   * Save image settings (delegates to SearchSettingsService)
   */
  private saveImageSettings(settings: { imageDisplayDuration: number; imageAutoAdvance: boolean; animatedGifBehavior: string; pinchZoomEnabled: boolean }): void {
    this.searchSettingsService.saveImageSettings(settings);
  }

  /**
   * Get current fullscreen controls position setting
   */
  getFullscreenControlsPosition(): string {
    return this.settingsForm.get('fullscreenControlsPosition')?.value || 'bottom-right';
  }

  /**
   * Get current image settings
   */
  getImageSettings(): { imageDisplayDuration: number; imageAutoAdvance: boolean; animatedGifBehavior: string; pinchZoomEnabled: boolean } {
    return {
      imageDisplayDuration: Math.max(1, Math.min(60, this.settingsForm.get('imageDisplayDuration')?.value || 5)),
      imageAutoAdvance: this.settingsForm.get('imageAutoAdvance')?.value !== false,
      animatedGifBehavior: this.settingsForm.get('animatedGifBehavior')?.value || 'once',
      pinchZoomEnabled: this.settingsForm.get('pinchZoomEnabled')?.value === true,
    };
  }

  isQueuePersistenceEnabled(): boolean {
    return this.settingsForm.get('persistQueue')?.value;
  }

  /**
   * Get current persistence settings
   */
  getPersistenceSettings(): { persistVolume: boolean } {
    return {
      persistVolume: this.settingsForm.get('persistVolume')?.value !== false,
    };
  }

  /**
   * Reset colors to default values
   */
  resetColorsToDefault(): void {
    this.settingsForm.patchValue({
      selectedTheme: '', // Reset theme selection
      primaryColor: this.DEFAULT_PRIMARY_COLOR,
      secondaryColor: this.DEFAULT_SECONDARY_COLOR,
      tertiaryColor: this.DEFAULT_TERTIARY_COLOR,
      backgroundColor: this.DEFAULT_BACKGROUND_COLOR,
    });

    this.colorThemeService.applyColorSettings(this.DEFAULT_PRIMARY_COLOR, this.DEFAULT_SECONDARY_COLOR, this.DEFAULT_TERTIARY_COLOR, this.DEFAULT_BACKGROUND_COLOR);
    this.colorThemeService.saveColorSettings(this.DEFAULT_PRIMARY_COLOR, this.DEFAULT_SECONDARY_COLOR, this.DEFAULT_TERTIARY_COLOR, this.DEFAULT_BACKGROUND_COLOR);
  }

  /**
   * Clear seen media history
   */
  clearSeenVideos(): void {
    this.seenPersistenceService.clearSeenVideos();
    console.log('Seen media history cleared');
  }

  /**
   * Nuke all settings and reload the application
   */
  nukeSettings(): void {
    // Clear all localStorage keys
    localStorage.clear();
    // Reload the SPA
    window.location.reload();
  }

  // Confirmation state for nuking settings
  isConfirmingNuke = false;

  /**
   * Handle nuke button click - toggle confirmation or execute nuke
   */
  /**
   * Handle persistence setting changes
   */
  onPersistenceChange(setting: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const isEnabled = target.checked;

    console.log(`Persistence setting changed: ${setting} = ${isEnabled}`);

    switch (setting) {
      case 'volume':
        this.searchSettingsService.savePersistenceSettings(isEnabled);
        break;
      case 'seenTracking': {
        const currentSeenSettings = this.seenPersistenceService.getSeenSettings();
        this.seenPersistenceService.saveSeenSettings({
          ...currentSeenSettings,
          enableSeenTracking: isEnabled,
        });
        break;
      }
      case 'seenBlacklist': {
        const currentSeenBlacklistSettings = this.seenPersistenceService.getSeenSettings();
        this.seenPersistenceService.saveSeenSettings({
          ...currentSeenBlacklistSettings,
          enableSeenBlacklist: isEnabled,
        });
        break;
      }
      case 'queue': {
        this.searchSettingsService.saveQueuePersistenceSetting(isEnabled);
        break;
      }
      // Future persistence settings can be added here
      default:
        console.warn(`Unknown persistence setting: ${setting}`);
    }
  }

  /**
   * Handle nuke button click
   */
  onNukeButtonClick(): void {
    console.log('Nuke button clicked. Current confirmation state:', this.isConfirmingNuke);
    if (this.isConfirmingNuke) {
      console.log('Confirmation received. Executing nukeSettings.');
      // Execute the nuke action only on the second click
      this.nukeSettings();
      this.isConfirmingNuke = false; // Reset the state after action
    } else {
      console.log('Entering confirmation mode. Waiting for second click.');
      // Enter confirmation mode
      this.isConfirmingNuke = true;
      setTimeout(() => {
        console.log('Confirmation timeout. Resetting state.');
        this.isConfirmingNuke = false;
      }, 5000); // Reset after 5 seconds if no confirmation
    }
  }

  // ─── Filter Groups ──────────────────────────────────────────────────────

  /** Toggle the filter groups panel. */
  toggleFilterGroups(): void {
    this.showFilterGroups = !this.showFilterGroups;
    if (this.showFilterGroups) {
      this.filterGroupSearch = '';
    }
  }

  /** Filtered custom groups based on search query. */
  get filteredCustomGroups(): FilterGroup[] {
    const term = this.filterGroupSearch.toLowerCase().trim();
    return this.allFilterGroups
      .filter(g => !g.isDefault)
      .filter(g => !term || g.name.toLowerCase().includes(term));
  }

  /** Filtered default groups based on search query. */
  get filteredDefaultGroups(): FilterGroup[] {
    const term = this.filterGroupSearch.toLowerCase().trim();
    return this.allFilterGroups
      .filter(g => g.isDefault)
      .filter(g => !term || g.name.toLowerCase().includes(term));
  }

  /** Apply a filter group's config to the search options form. */
  applyFilterGroup(group: FilterGroup): void {
    this.searchOptionsForm.patchValue(group.config);
    if (group.config.tags) {
      this.searchForm.patchValue({ query: group.config.tags });
    }
    // A positive periodOffset implies date iteration has already started
    // going backwards, so auto-advance should continue in that direction.
    if (group.config.advancedDateMode === 'period' && group.config.periodOffset > 0) {
      this.lastDateShiftDirection = 'prev';
    }
    this.showFilterGroups = false;
    this.onSearch();
  }

  /** Save the current search options form state as a new custom filter group. */
  startAddFilterGroup(): void {
    this.isAddingFilterGroup = true;
    this.newFilterGroupName = '';
  }

  /** Confirm adding a new custom filter group. */
  confirmAddFilterGroup(): void {
    const name = this.newFilterGroupName.trim();
    if (name) {
      const config = this.searchOptionsForm.value;
      this.filterGroupService.addCustomGroup(name, config);
    }
    this.isAddingFilterGroup = false;
    this.newFilterGroupName = '';
  }

  /** Cancel adding a new custom filter group. */
  cancelAddFilterGroup(): void {
    this.isAddingFilterGroup = false;
    this.newFilterGroupName = '';
  }

  /** Start renaming a custom filter group. */
  startRenameGroup(group: FilterGroup): void {
    this.renamingGroupId = group.id;
    this.renamingGroupName = group.name;
    setTimeout(() => {
      const input = document.querySelector('.fg-rename-input') as HTMLInputElement;
      if (input) input.focus();
    }, 0);
  }

  /** Confirm the rename. */
  confirmRenameGroup(): void {
    if (this.renamingGroupId && this.renamingGroupName.trim()) {
      this.filterGroupService.renameCustomGroup(this.renamingGroupId, this.renamingGroupName.trim());
    }
    this.renamingGroupId = null;
    this.renamingGroupName = '';
  }

  /** Cancel the rename. */
  cancelRenameGroup(): void {
    this.renamingGroupId = null;
    this.renamingGroupName = '';
  }

  /** Handle blur on rename input with delay to avoid race condition with action buttons. */
  onRenameBlur(): void {
    setTimeout(() => {
      if (this.renamingGroupId) {
        this.confirmRenameGroup();
      }
    }, 150);
  }

  /** Delete a custom filter group. */
  deleteFilterGroup(group: FilterGroup): void {
    this.filterGroupService.removeCustomGroup(group.id);
  }

  /**
   * Smooth scroll to the top of the video player
   */
  private scrollToVideoPlayer(): void {
    // Use setTimeout to ensure the video player is rendered
    setTimeout(() => {
      const videoPlayer = document.querySelector('app-video-player');
      if (videoPlayer) {
        videoPlayer.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  }

  activeSettingsTab = 'api';

  setSettingsTab(tab: string): void {
    this.activeSettingsTab = tab;
  }

  apiTestStatus: 'success' | 'error' | 'pending' | null = null;
  apiTestMessage = '';

  /**
   * Test API credentials by making a lightweight authenticated request
   */
  onTestApi(): void {
    this.apiTestStatus = 'pending';
    this.apiTestMessage = 'Testing...';
    const username = this.settingsForm.get('username')?.value?.trim();
    const apiKey = this.settingsForm.get('apiKey')?.value?.trim();
    if (!username || !apiKey) {
      this.apiTestStatus = 'error';
      this.apiTestMessage = 'Please enter both username and API key.';
      return;
    }
    // Use e621 /users/me.json endpoint for credential validation
    this.e621Service.testApiCredentials(username, apiKey).subscribe({
      next: (result) => {
        if (result) {
          this.apiTestStatus = 'success';
          this.apiTestMessage = 'API credentials are valid!';
        } else {
          this.apiTestStatus = 'error';
          this.apiTestMessage = 'API credentials are invalid.';
        }
      },
      error: (err) => {
        this.apiTestStatus = 'error';
        this.apiTestMessage = 'API test failed: ' + (err?.error?.message || err?.message || 'Unknown error');
      }
    });
  }
}
